import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import type { AccountEmailKind } from '../api/customers-api';
import { toAccountEmailBody } from '../api/mappers';
import { useSendAccountEmail } from '../hooks/use-customer-mutations';
import {
  accountEmailSchema,
  type AccountEmailFormInput,
  type AccountEmailFormOutput,
} from '../schemas/customer-form-schema';

export interface AccountEmailDialogProps {
  storeId: string;
  customer: Customer;
  kind: AccountEmailKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A guest gets an invitation to set a password; an account holder gets a reset link. */
export function AccountEmailDialog({
  storeId,
  customer,
  kind,
  open,
  onOpenChange,
}: AccountEmailDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const send = useSendAccountEmail(storeId, customer.id, kind);
  const form = useForm<AccountEmailFormInput, unknown, AccountEmailFormOutput>({
    resolver: useI18nResolver(accountEmailSchema),
    defaultValues: { resetUrl: '' },
  });

  const close = () => {
    onOpenChange(false);
    send.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await send.mutateAsync(toAccountEmailBody(values));
      close();
    } catch (error) {
      if (error instanceof ApiError && error.fieldErrors['body.resetUrl']) {
        // The other refusal on this field is the 422 for a store with no storefront address.
        form.setError('resetUrl', {
          type: 'server',
          message:
            error.code === 'ORIGIN_NOT_ALLOWED'
              ? message(error)
              : t('customers:accountEmail.noStorefront'),
        });
        return;
      }
      const disabled = error instanceof ApiError && error.status === 409;
      form.setError('root.server', {
        type: 'server',
        message: disabled ? t('customers:accountEmail.disabled') : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`customers:accountEmail.${kind}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`customers:accountEmail.${kind}.description`, { email: customer.email })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="resetUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:accountEmail.resetUrl')}</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      autoComplete="off"
                      placeholder="https://shop.example.com/account/reset"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('customers:accountEmail.resetUrlHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {send.isPending
                  ? t('customers:accountEmail.sending')
                  : t(`customers:accountEmail.${kind}.submit`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
